import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import { useAssessmentPlanFilterOptions } from "../../../hooks/useAssessmentPlanFilterOptions";
import {
  buildConditionalPlansQuery,
  fetchConditionalPlans,
  mapConditionalPlanToRuleRow,
} from "../../../services/autoTaggingService";
import {
  AI_PLAN_ADD_ENHANCED_TAGGING_RULE_PATH,
  buildEditEnhancedTaggingRulePath,
} from "../constants";
import { formatConditionToNaturalLanguage } from "./formatConditionNaturalLanguage";
import EnhancedTaggingRuleFilterBar from "./EnhancedTaggingRuleFilterBar";
import EnhancedTaggingRuleTable from "./EnhancedTaggingRuleTable";
import type { EnhancedTaggingRuleRow } from "./types";

export default function EnhancedTaggingRuleTab() {
  const navigate = useNavigate();
  const { showMessage } = useSnackbar();
  const { planTypes, planTypesLoading } = useAssessmentPlanFilterOptions();
  const [objectType, setObjectType] = useState("");
  const [objectSubType, setObjectSubType] = useState("");
  const [dimension, setDimension] = useState("");
  const [value, setValue] = useState("");
  const [rows, setRows] = useState<EnhancedTaggingRuleRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);

  const loadRows = useCallback(() => {
    let mounted = true;
    setLoading(true);

    const query = buildConditionalPlansQuery({
      objectType,
      objectSubType,
      concept: dimension,
      conceptValueCode: value,
    });

    fetchConditionalPlans(query)
      .then((result) => {
        if (!mounted) return;
        setRows(
          result.items.map((plan) =>
            mapConditionalPlanToRuleRow(plan, {
              conditionDescription: formatConditionToNaturalLanguage(
                plan.condition,
              ),
              planTypes,
            }),
          ),
        );
      })
      .catch(() => {
        if (!mounted) return;
        setRows([]);
        showMessage("Failed to load enhanced tagging rules.", "error");
      })
      .finally(() => {
        if (!mounted) return;
        setLoading(false);
        setHasLoaded(true);
      });

    return () => {
      mounted = false;
    };
  }, [dimension, objectSubType, objectType, planTypes, showMessage, value]);

  useEffect(() => {
    const cleanup = loadRows();
    return cleanup;
  }, [loadRows]);

  const handleEdit = useCallback(
    (row: EnhancedTaggingRuleRow) => {
      navigate(buildEditEnhancedTaggingRulePath(row.id), {
        state: { rule: row },
      });
    },
    [navigate],
  );

  // Keep the grid mounted on refresh so pagination is preserved.
  const showInitialLoading = (!hasLoaded && loading) || planTypesLoading;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "16px",
        flex: 1,
        minHeight: 0,
        mx: "16px",
      }}
    >
      <EnhancedTaggingRuleFilterBar
        objectType={objectType}
        objectSubType={objectSubType}
        dimension={dimension}
        value={value}
        onObjectTypeChange={(nextObjectType, nextObjectSubType) => {
          setObjectType(nextObjectType);
          setObjectSubType(nextObjectSubType);
          setDimension("");
          setValue("");
        }}
        onObjectSubTypeChange={setObjectSubType}
        onDimensionChange={setDimension}
        onValueChange={setValue}
      />
      {showInitialLoading ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: 320,
          }}
        >
          <CircularProgress size={28} />
        </Box>
      ) : (
        <EnhancedTaggingRuleTable
          rows={rows}
          onAddNew={() => navigate(AI_PLAN_ADD_ENHANCED_TAGGING_RULE_PATH)}
          onEdit={handleEdit}
          onRuleUpdated={loadRows}
        />
      )}
    </Box>
  );
}
